import { FC, useCallback } from 'react';
import {
  ActionButton,
  confirmationHeaders,
  DESCRIPTION_MAX_LENGTH,
  DetailLayout,
  EditLock,
  Form,
  FormAlerts,
  FormField,
  FormInput,
  GetApiErrorMessage,
  InfoIconUrl,
  NAME_MAX_LENGTH,
  ObjectHeader,
  ObjectType,
  Padding,
  Stack,
  AccessGroupDto,
  AccessGroupValidFormData,
  useMutateAccessGroup,
  useMutationErrors,
  useAlertContext,
  AccessGroupValidFormDataSchema,
  useNavigationBlockers,
  validationTranslations,
} from '@atlas-frontend/sdk';
import { i18nNsFactory } from '@atlas-frontend/i18n';
import translations from './AccessGroupForm.translations.json';
import { AccessGroupBreadcrumbs } from './AccessGroupBreadcrumbs';
import { useWatch } from 'react-hook-form';

const { addTranslations, useTranslation, t } =
  i18nNsFactory('access-group-form');
addTranslations(translations);
addTranslations(validationTranslations);

const translateMutationError: GetApiErrorMessage = (code, meta) =>
  t('alert.error.code.' + code, meta?.args && { ...meta.args });

export type AccessGroupFormProps =
  | {
      mode: 'create';
      id?: never;
      defaultValues: AccessGroupDto;
      onClickCancel: () => void;
      onSuccess?: (response: AccessGroupDto) => void;
    }
  | {
      mode: 'edit';
      id: number;
      defaultValues: AccessGroupDto;
      onClickCancel: () => void;
      onSuccess?: (response: AccessGroupDto) => void;
    };

export const AccessGroupForm: FC<AccessGroupFormProps> = (props) => {
  const { t } = useTranslation();
  const { mode, id, defaultValues, onClickCancel, onSuccess } = props;
  const { mutateAsync } = useMutateAccessGroup();
  const { mutationErrors, setMutationErrors, clearMutationErrors } =
    useMutationErrors(translateMutationError);
  const { resetAlert, setSuccess } = useAlertContext();
  const { clear: clearBlockers } = useNavigationBlockers();

  const handleFinish = useCallback(
    (values: AccessGroupValidFormData, confirm: boolean) => {
      resetAlert();
      clearMutationErrors();
      return mutateAsync({
        id: mode === 'edit' ? id : undefined,
        data: values,
        headers: confirmationHeaders(confirm),
      })
        .then((responseData) => {
          if (responseData) {
            if (Object.hasOwn(responseData, 'id')) {
              clearBlockers();
              onSuccess?.(responseData);
              setSuccess(
                mode === 'edit' ? undefined : t('alert.success.created'),
              );
            } else {
              throw new Error('Expecting "id" in response data.');
            }
          }
        })
        .catch((error) => {
          setMutationErrors(error.response?.data.errors ?? []);
          throw error;
        });
    },
    [
      resetAlert,
      clearMutationErrors,
      mutateAsync,
      mode,
      id,
      clearBlockers,
      setSuccess,
      t,
      onSuccess,
      setMutationErrors,
    ],
  );

  return (
    <Form
      schema={AccessGroupValidFormDataSchema}
      defaultValues={defaultValues}
      serverErrors={mutationErrors}
      onSubmit={handleFinish}
    >
      <DetailLayout
        infoIconUrl={InfoIconUrl.ACCESS_GROUPS_DETAILS}
        breadCrumb={<AccessGroupBreadcrumbs data={defaultValues} />}
        side={{
          header: <AccessGroupHeader />,
          content: (
            <Padding>
              <Stack size="md">
                <FormField
                  name="name"
                  label={t('name.label')}
                  maxLength={NAME_MAX_LENGTH}
                  required
                />
                <FormField name="description" label={t('description.label')}>
                  <FormInput
                    name="description"
                    rows={2}
                    autoSize
                    maxLength={DESCRIPTION_MAX_LENGTH}
                  />
                </FormField>
              </Stack>
            </Padding>
          ),
          footer:
            mode === 'edit' ? (
              <EditLock
                id={defaultValues.id}
                entity={defaultValues}
                objectType={ObjectType.AccessGroup}
                onTimeout={onClickCancel}
              />
            ) : undefined,
        }}
        headerConfig={{
          actions: (
            <>
              <ActionButton type="abort" onClick={onClickCancel} />
              <ActionButton type="formSubmit" />
            </>
          ),
        }}
        alert={<FormAlerts />}
      />
    </Form>
  );
};

const AccessGroupHeader = () => {
  const name = useWatch({ name: 'name' });

  return (
    <Padding>
      <ObjectHeader lowerText={name} upperPlaceholder="" />
    </Padding>
  );
};
